import React, { useEffect, useState } from "react";
import { Form } from "antd";
import { useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTicketAlt } from "@fortawesome/free-solid-svg-icons";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  SyncOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import VALIDATE_PROMO_CODE from "graphql/mutations/ValidatePromoCode";
import capitalizeFirstLetters from "../utils/capitalizeFirstLetter";
import {
  InlineInput,
  ApplyButton,
  CouponDescription,
  WhenMismatchedCurrency,
  StyledTag,
} from "./styles";
import "./PromoCode.css";

type TProps = {
  productId: string;
  inputVisible: boolean;
  hideInput: () => void;
  showInput: () => void;
  setDiscount: (number: number) => void;
  setValidReferralCodeMessage: (value: string) => void;
  hasFreeTrial: boolean;
  classPrice: number;
  currency: string;
};
const PROMO_CODE_FIELD_NAME = "promoCode";
const PromoCodeInput = ({
  productId,
  inputVisible,
  hideInput,
  showInput,
  setDiscount,
  setValidReferralCodeMessage,
  hasFreeTrial,
  classPrice,
  currency,
}: TProps) => {
  const { t } = useTranslation("new_sign_up");
  const [validatePromoCode, { data: promoCodeData }] =
    useMutation(VALIDATE_PROMO_CODE);
  const [matchedCurrency, setMatchedCurrency] = useState(false); // must be false if coupon not valid]
  const promoCodeValidator = async (_: any, value: string | undefined) => {
    if (!value) return;

    hideInput();
    try {
      await validatePromoCode({
        variables: {
          productId,
          promoCode: value,
        },
      });
    } catch (e) {
      throw new Error(t("Invalid promotion code"));
    }
  };

  const handleDiscountCode = React.useCallback(() => {
    // if null currency in validateCoupon, this is a percentOff coupon and matchedCurrency is auto true
    const isCurrencyMatched =
      !promoCodeData?.validateCoupon.coupon.currency ||
      currency === promoCodeData?.validateCoupon?.coupon?.currency;
    setMatchedCurrency(isCurrencyMatched);
    if (isCurrencyMatched) {
      if (promoCodeData.validateCoupon.coupon.amountOff)
        setDiscount(promoCodeData.validateCoupon.coupon.amountOff / 100);
      // stripe doesn't include dot before 2 decimals
      else
        setDiscount(
          (promoCodeData.validateCoupon.coupon.percentOff * classPrice) / 100
        );
    }
  }, [promoCodeData, classPrice, currency, setDiscount]);

  const handleReferralCode = React.useCallback(() => {
    if (promoCodeData?.validateCoupon.coupon.referralName) {
      const referralName = promoCodeData?.validateCoupon.coupon.referralName;
      setValidReferralCodeMessage(
        `${capitalizeFirstLetters(referralName)}'s ${t(
          "referral code has been successfully applied. You will receive credits for"
        )} ${hasFreeTrial ? t("a total of") : t("an extra")} ${t(
          "5 free group classes."
        )}`
      );
    }
  }, [promoCodeData, setValidReferralCodeMessage, hasFreeTrial, t]);

  useEffect(() => {
    if (promoCodeData?.validateCoupon.coupon.valid) {
      if (promoCodeData?.validateCoupon.coupon.type === "coupon") {
        handleDiscountCode();
      }
      if (promoCodeData?.validateCoupon.coupon.type === "referral_code") {
        // Set matchedCurrency to true, as the referral code is not affected by the currency.
        setMatchedCurrency(true);
        handleReferralCode();
      }
    } else {
      setDiscount(0);
    }
  }, [
    promoCodeData,
    handleDiscountCode,
    handleReferralCode,
    setMatchedCurrency,
    setDiscount,
  ]);

  return (
    <div>
      {/* Part 1: Display input */}
      <Form.Item noStyle shouldUpdate>
        {({ validateFields }) => {
          const handleEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
            e.preventDefault();
            hideInput();
            validateFields(["promoCode"]);
          };
          return (
            <>
              <Form.Item
                name="promoCode"
                noStyle
                rules={[{ validator: promoCodeValidator }]}
                validateTrigger={["onBlur"]}
                hidden={!inputVisible}
              >
                <InlineInput
                  prefix={<FontAwesomeIcon icon={faTicketAlt as IconProp} />}
                  type={inputVisible ? "text" : "hidden"}
                  autoFocus
                  placeholder={t("Enter Discount Code")}
                  onPressEnter={handleEnter}
                  size="large"
                  style={{ width: "50%" }}
                />
              </Form.Item>
              {inputVisible && (
                <ApplyButton
                  size="small"
                  onClick={() => {
                    validateFields(["promoCode"]);
                  }}
                >
                  {t("APPLY")}
                </ApplyButton>
              )}
            </>
          );
        }}
      </Form.Item>
      {/* Part 2: Show tag */}
      <Form.Item noStyle shouldUpdate>
        {({
          getFieldValue,
          setFieldsValue,
          getFieldError,
          isFieldValidating,
        }) => {
          const promoCode: string | undefined = getFieldValue(
            PROMO_CODE_FIELD_NAME
          );
          const errors = getFieldError(PROMO_CODE_FIELD_NAME);
          const validating: boolean = isFieldValidating(PROMO_CODE_FIELD_NAME);
          const handleRemoveCode = () => {
            setFieldsValue({ promoCode: undefined });
            showInput();
          };
          const checkColor = () => {
            if (validating) return "default";
            if (errors.length > 0 || !matchedCurrency) return "warning";
            if (errors.length === 0) return "processing";
            return "default";
          };

          const getColor = () => {
            if (validating) return "gray";
            if (errors.length > 0 || !matchedCurrency) return "orange";
            if (errors.length === 0) return "#1070b6";
            return "default";
          };

          const tagColor = checkColor();
          const wholeTagColor = () => {
            if (tagColor === "default") return "rgb(240, 240, 240)";
            if (tagColor === "warning") return "#fad8b7";
            return "#d3e8ff";
          };

          const renderIcon = () => {
            if (validating)
              return <SyncOutlined style={{ fontSize: "160%" }} />;
            if (errors.length > 0 || !matchedCurrency)
              // either invalid coupon or different currency
              return <ExclamationCircleOutlined style={{ fontSize: "160%" }} />;
            if (errors.length === 0)
              return <CheckCircleOutlined style={{ fontSize: "160%" }} />;
            return null;
          };

          const renderMessage = () => {
            if (validating) return { message: "", color: "gray" };
            if (errors.length > 0) {
              return { message: "Invalid Code", color: "orange" };
            }
            if (!matchedCurrency) {
              return {
                message: "Wrong Currency",
                color: "orange",
              };
            }
            return { message: "Success!", color: "#1070b6" };
          };

          if (!inputVisible && promoCode) {
            return (
              <>
                <StyledTag
                  color={tagColor}
                  closable
                  onClose={() => {
                    handleRemoveCode();
                    setValidReferralCodeMessage("");
                  }}
                  icon={renderIcon()}
                  style={{
                    backgroundColor: wholeTagColor(),
                    border: `3px solid ${getColor()}`,
                    color: getColor(),
                  }}
                >
                  {promoCode}
                </StyledTag>
                <CouponDescription
                  style={{ color: renderMessage().color, width: "35%" }}
                >
                  {renderMessage().message}
                </CouponDescription>
                {promoCodeData?.validateCoupon.coupon.valid &&
                !matchedCurrency ? (
                  <WhenMismatchedCurrency>
                    {t(
                      "The discount and your account currency do not match. To use this discount code, register a new account with a subscription for that currency."
                    )}
                  </WhenMismatchedCurrency>
                ) : null}
              </>
            );
          }
          return null;
        }}
      </Form.Item>
    </div>
  );
};

export default PromoCodeInput;
